import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import SurahList from './components/SurahList';
import SurahDetail from './components/SurahDetail';
import JuzDetail from './components/JuzDetail';
import JuzList from './components/JuzList';
import SavedScreen from './components/SavedScreen';
import AudioProvider from './providers/AudioProvider';
import GlobalPlayer from './components/GlobalPlayer';
import ThemeToggle from './components/ThemeToggle';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import BookmarksNotesPanel from './components/BookmarksNotesPanel';
import SearchModal from './components/SearchModal';
import IndexModal from './components/IndexModal';
import BottomNavigation from './components/BottomNavigation';
import useAudioStore from './store/audioStore';
import { useBookmarks } from './hooks/useBookmarks';
import { useNotes } from './hooks/useNotes';
import { useLastRead } from './hooks/useLastRead';
import SyncService from './services/SyncService';

function App() {
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasHistory, setHasHistory] = useState(false);
  const [lastListening, setLastListening] = useState(null);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showIndexModal, setShowIndexModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('quran_active_tab') || 'surahs');

  const { resumeFromLocalStorage, loadFromLocalStorage } = useAudioStore();
  const { hasBookmarks } = useBookmarks();
  const { hasNotes } = useNotes();
  const { lastRead, hasLastRead } = useLastRead();

  useEffect(() => {
    fetchSurahs();

    // Pull latest cloud state and merge into localStorage.
    // SyncService.pull() is a no-op when the user is not authenticated,
    // so it is safe to call unconditionally here.
    SyncService.pull();

    // Check for listening history
    const saved = loadFromLocalStorage();
    if (saved && saved.surah && saved.mode !== 'revelation-order') {
      setHasHistory(true);
      setLastListening(saved);
    }

    // Fix iOS viewport height
    const setAppHeight = () => {
      const vh = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${vh}px`);
    };

    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
    };
  }, [loadFromLocalStorage]);

  useEffect(() => {
    localStorage.setItem('quran_active_tab', activeTab);
  }, [activeTab]);

  const fetchSurahs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/surahs');

      if (!response.ok) {
        throw new Error('Failed to fetch surahs');
      }

      const data = await response.json();
      setSurahs(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching surahs:', err);
      setError(err.message || 'Failed to load surahs');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = () => {
    resumeFromLocalStorage();
  };

  return (
    <Router>
      <AudioProvider>
        {/* Global Audio Player - Always rendered when audio is active */}
        <GlobalPlayer />

        <Routes>
          {/* List view route */}
          <Route
            path="/"
            element={
              <div
                className="flex flex-col"
                style={{
                  background: 'var(--bg-primary)',
                  minHeight: 'var(--app-height)',
                  paddingTop: 'var(--safe-area-top)',
                  paddingBottom: 'var(--safe-area-bottom)',
                }}
              >
                {/* Top Navigation Bar */}
                <nav
                  className="sticky top-0 z-50 backdrop-blur-md"
                  style={{
                    background: 'var(--bg-overlay)',
                    borderBottom: '1px solid var(--border-subtle)',
                    paddingTop: 'var(--safe-area-top)',
                  }}
                >
                  <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="text-xl font-bold text-arabic" style={{ color: 'var(--text-arabic)' }}>
                      القرآن
                    </div>

                    <div className="hidden md:flex items-center gap-2 md:gap-3">
                      {/* Search Button - Desktop Only */}
                      <button
                        onClick={() => setShowSearchModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-all active:scale-95"
                        style={{
                          background: 'rgba(148, 163, 184, 0.08)',
                          border: '1px solid var(--border-default)',
                          color: 'var(--text-secondary)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gold-muted)';
                          e.currentTarget.style.borderColor = 'var(--border-gold)';
                          e.currentTarget.style.color = 'var(--gold-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                          e.currentTarget.style.borderColor = 'var(--border-default)';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Search Surahs"
                      >
                        <span className="hidden sm:inline">🔍</span>
                        <span className="sm:hidden">🔍</span>
                        <span className="hidden md:inline text-sm font-medium">Search</span>
                      </button>

                      {/* Index Button - Desktop Only */}
                      <button
                        onClick={() => setShowIndexModal(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-all active:scale-95"
                        style={{
                          background: 'rgba(148, 163, 184, 0.08)',
                          border: '1px solid var(--border-default)',
                          color: 'var(--text-secondary)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gold-muted)';
                          e.currentTarget.style.borderColor = 'var(--border-gold)';
                          e.currentTarget.style.color = 'var(--gold-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                          e.currentTarget.style.borderColor = 'var(--border-default)';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                        }}
                        title="Browse All Surahs"
                      >
                        <span className="hidden sm:inline">📖</span>
                        <span className="sm:hidden">📖</span>
                        <span className="hidden md:inline text-sm font-medium">Index</span>
                      </button>

                      {/* Bookmarks/Notes Button - Desktop Only */}
                      {(hasBookmarks || hasNotes) && (
                        <button
                          onClick={() => setShowBookmarksPanel(true)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-all active:scale-95"
                          style={{
                            background: 'rgba(148, 163, 184, 0.08)',
                            border: '1px solid var(--border-default)',
                            color: 'var(--text-secondary)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--gold-muted)';
                            e.currentTarget.style.borderColor = 'var(--border-gold)';
                            e.currentTarget.style.color = 'var(--gold-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                            e.currentTarget.style.borderColor = 'var(--border-default)';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                          title="Bookmarks & Notes"
                        >
                          <span className="hidden sm:inline">📚</span>
                          <span className="sm:hidden">📚</span>
                          <span className="hidden md:inline text-sm font-medium">Saved</span>
                        </button>
                      )}

                      {/* Theme Toggle */}
                      <ThemeToggle />
                    </div>
                  </div>
                </nav>

                {/* Bookmarks/Notes Panel */}
                <BookmarksNotesPanel
                  isOpen={showBookmarksPanel}
                  onClose={() => setShowBookmarksPanel(false)}
                />

                {/* Search Modal */}
                <SearchModal
                  isOpen={showSearchModal}
                  onClose={() => setShowSearchModal(false)}
                  surahs={surahs}
                />

                {/* Index Modal */}
                <IndexModal
                  isOpen={showIndexModal}
                  onClose={() => setShowIndexModal(false)}
                  surahs={surahs}
                />

                {/* Hero Section */}
                <header
                  className="relative overflow-hidden animate-gradient-flow"
                  style={{
                    minHeight: '35vh',
                    background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, var(--bg-primary) 100%)',
                  }}
                >
                  <div className="max-w-6xl mx-auto px-4 py-12 md:py-16 text-center relative z-10">
                    {/* Title */}
                    <h1 className="text-arabic text-5xl md:text-7xl font-bold mb-3"
                      style={{
                        color: 'var(--text-arabic)',
                        letterSpacing: '0.02em',
                        lineHeight: '1.2'
                      }}>
                      📖 القرآن الكريم
                    </h1>

                    <p className="text-lg md:text-xl mb-12"
                      style={{
                        color: 'var(--text-urdu)',
                        opacity: '0.85'
                      }}>
                      The Noble Quran in Revelation Order
                    </p>

                    {/* Action Buttons */}
                    {!loading && !error && surahs.length > 0 && hasHistory && lastListening && (
                      <div className="flex flex-col gap-4 max-w-md mx-auto w-full">
                        {/* Resume Button */}
                        <button
                          onClick={handleResume}
                          className="group relative overflow-hidden rounded-2xl transition-all duration-300 active:scale-[0.98]"
                          style={{
                            background: 'var(--bg-elevated)',
                            border: '2px solid var(--border-gold)',
                            boxShadow: 'var(--shadow-md)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--gold-muted)';
                            e.currentTarget.style.borderColor = 'var(--gold-hover)';
                            e.currentTarget.style.boxShadow = '0 8px 24px var(--gold-glow)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--bg-elevated)';
                            e.currentTarget.style.borderColor = 'var(--border-gold)';
                            e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                          }}
                        >
                          <div className="flex items-center gap-4 px-6 py-4">
                            {/* Headphones Icon */}
                            <div
                              className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                              style={{
                                background: 'var(--gold-muted)',
                                border: '2px solid var(--border-gold)',
                              }}
                            >
                              <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                style={{ color: 'var(--gold-hover)' }}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                            </div>

                            {/* Text Content */}
                            <div className="flex-1 text-left">
                              <div
                                className="font-bold text-base"
                                style={{ color: 'var(--gold-hover)' }}
                              >
                                Resume Listening
                              </div>
                              <div
                                className="text-sm mt-1"
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                Continue from Surah {lastListening.surah}
                              </div>
                            </div>

                            {/* Arrow Icon */}
                            <svg
                              className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{ color: 'var(--gold-hover)' }}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </header>

                {/* Main Content */}
                <main
                  className="flex-1 max-w-6xl mx-auto w-full px-4 py-12"
                  style={{
                    paddingBottom: 'calc(3rem + var(--safe-area-bottom))',
                  }}
                >
                  {/* Loading State */}
                  {loading && (
                    <div className="text-center py-20">
                      <div className="inline-block animate-shimmer rounded-xl p-8 mb-4">
                        <div className="text-5xl">📖</div>
                      </div>
                      <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>
                        Loading surahs...
                      </p>
                    </div>
                  )}

                  {/* Error State */}
                  {error && (
                    <div className="p-6 rounded-lg border-l-4 text-center"
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderColor: 'var(--error)',
                      }}>
                      <p className="font-semibold" style={{ color: 'var(--error)' }}>
                        Error: {error}
                      </p>
                    </div>
                  )}

                  {/* Resume Reading */}
                  {!loading && !error && hasLastRead && lastRead && (
                    <Link
                      to={
                        lastRead.contextMode === 'juz'
                          ? `/juz/${lastRead.juzNumber}?ayah=${lastRead.ayahNumber}&mode=${lastRead.readingMode}`
                          : `/surah/${lastRead.surahNumber}?ayah=${lastRead.ayahNumber}&mode=${lastRead.readingMode}`
                      }
                      className="block mb-8 p-4 rounded-xl transition-all active:scale-[0.98]"
                      style={{
                        background: 'var(--gold-muted)',
                        border: '2px solid var(--border-gold)',
                        boxShadow: 'var(--shadow-sm)',
                      }}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{
                            background: 'var(--bg-elevated)',
                            border: '2px solid var(--border-gold)',
                          }}
                        >
                          <span className="text-2xl">📖</span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-bold" style={{ color: 'var(--gold-hover)' }}>
                            Resume Reading
                          </div>
                          <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                            {lastRead.contextMode === 'juz'
                              ? `Continue from Juz ${lastRead.juzNumber}, Ayah ${lastRead.ayahNumber}`
                              : `Continue from Surah ${lastRead.surahNumber}, Ayah ${lastRead.ayahNumber}`}
                          </div>
                        </div>
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--gold-hover)' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  )}

                  {/* Surahs / Juz / Saved Tab Toggle */}
                  {!loading && !error && (
                    <div className="flex gap-1 mb-8 p-1 rounded-xl w-fit" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
                      {[
                        { id: 'surahs', label: 'Surahs' },
                        { id: 'juz', label: 'Juz (Saparah)' },
                        { id: 'saved', label: 'Saved' },
                      ].map(({ id, label }) => (
                        <button
                          key={id}
                          onClick={() => setActiveTab(id)}
                          className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                          style={{
                            background: activeTab === id ? 'var(--gold-muted)' : 'transparent',
                            border: activeTab === id ? '1px solid var(--border-gold)' : '1px solid transparent',
                            color: activeTab === id ? 'var(--gold-hover)' : 'var(--text-secondary)',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Surah Grid */}
                  {!loading && !error && surahs.length > 0 && activeTab === 'surahs' && (
                    <SurahList surahs={surahs} />
                  )}

                  {/* Juz Grid */}
                  {!loading && !error && activeTab === 'juz' && (
                    <JuzList />
                  )}

                  {/* Saved Screen */}
                  {!loading && !error && activeTab === 'saved' && (
                    <SavedScreen />
                  )}
                </main>

                {/* Footer */}
                <footer className="py-8 mt-16 border-t mb-20 md:mb-0"
                  style={{
                    background: 'var(--bg-secondary)',
                    borderColor: 'var(--border-subtle)',
                  }}>
                  <div className="max-w-6xl mx-auto px-4 text-center text-sm"
                    style={{ color: 'var(--text-secondary)' }}>
                    <p>Built with React | Powered by Al-Quran Cloud API</p>
                    <p className="mt-2 text-xs" style={{ opacity: '0.6' }}>
                      Read the Quran in Nazooli (Revelation) Order
                    </p>
                    <div className="mt-4 hidden md:flex justify-center">
                      <KeyboardShortcutsHelp />
                    </div>
                  </div>
                </footer>

                {/* Bottom Navigation - Mobile Only */}
                <BottomNavigation
                  onSearchClick={() => setShowSearchModal(true)}
                  onIndexClick={() => setShowIndexModal(true)}
                  hasBookmarks={hasBookmarks || hasNotes}
                  onBookmarksClick={() => setShowBookmarksPanel(true)}
                />
              </div>
            }
          />

          {/* Detail view route */}
          <Route path="/surah/:number" element={<SurahDetail />} />

          {/* Juz view route */}
          <Route path="/juz/:number" element={<JuzDetail />} />
        </Routes>
      </AudioProvider>
    </Router>
  );
}

export default App;
